import { useRef } from "react";
import AnchoredPanel from "../../../../GlobalComponents/AnchoredPanel/AnchoredPanel";

let listIdCounter = 0;

/**
 * Editor for one tag path, anchored under the cell that opened it.
 *
 * A path is a run of `{ key, value }` pairs — `module: Energy`,
 * `category: Electricity Consumption`, `kpi: Grid Electricity`. Keys come from
 * a known set, values are suggested from the keyword vocabulary but not
 * restricted to it, because production values routinely sit outside it. That
 * is what `<datalist>` gives for free.
 *
 * Not a modal: it does not trap focus or dim the page, so the row it belongs
 * to stays readable while a path is edited.
 *
 * @param {object} props
 * @param {{ name: string|null, pairs: { key: string, value: string }[] }} props.group
 * @param {boolean} props.named whether this row stores tags as named paths
 * @param {string[]} props.tagKeys
 * @param {Record<string, string[]>} props.tagValuesByKey
 * @param {(group: object) => void} props.onChange
 * @param {() => void} props.onRemove
 * @param {() => void} props.onClose
 */
export default function TagGroupEditor({
  anchorRef,
  group,
  named,
  tagKeys,
  tagValuesByKey,
  onChange,
  onRemove,
  onClose,
}) {
  const listIdRef = useRef(null);
  if (listIdRef.current === null) listIdRef.current = ++listIdCounter;


  const setPair = (index, patch) => {
    const pairs = group.pairs.map((pair, i) => (i === index ? { ...pair, ...patch } : pair));
    onChange({ ...group, pairs });
  };

  const removePair = (index) => {
    onChange({ ...group, pairs: group.pairs.filter((pair, i) => i !== index) });
  };

  const addPair = () => {
    onChange({ ...group, pairs: [...group.pairs, { key: tagKeys[0] || "", value: "" }] });
  };

  return (
    <AnchoredPanel anchorRef={anchorRef} onClose={onClose} className="kgb-picker kgb-picker--wide">
      {named ? (
        <label className="kgb-editor-row">
          <span className="kgb-editor-label">Group</span>
          <input
            type="text"
            className="kgb-editor-input"
            value={group.name || ""}
            placeholder="scope, energy, emission…"
            onChange={(e) => onChange({ ...group, name: e.target.value })}
          />
        </label>
      ) : (
        <p className="kgb-picker-title">Unnamed path</p>
      )}

      {group.pairs.map((pair, index) => (
        <div className="kgb-editor-row" key={`${index}-${pair.key}`}>
          <select
            className="kgb-editor-key"
            value={pair.key}
            aria-label="Tag key"
            onChange={(e) => setPair(index, { key: e.target.value })}
          >
            {tagKeys.includes(pair.key) ? null : <option value={pair.key}>{pair.key}</option>}
            {tagKeys.map((key) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="kgb-editor-input"
            value={pair.value}
            aria-label={`Value for ${pair.key}`}
            list={`kgb-tagvalues-${listIdRef.current}-${index}`}
            onChange={(e) => setPair(index, { value: e.target.value })}
          />
          <datalist id={`kgb-tagvalues-${listIdRef.current}-${index}`}>
            {(tagValuesByKey[pair.key] || []).map((value) => (
              <option key={value} value={value} />
            ))}
          </datalist>
          <button
            type="button"
            className="kgb-editor-remove"
            aria-label={`Remove ${pair.key}`}
            onClick={() => removePair(index)}
          >
            ×
          </button>
        </div>
      ))}

      <div className="kgb-editor-actions">
        <button type="button" className="kgb-btn kgb-btn--quiet" onClick={addPair}>
          Add pair
        </button>
        <button type="button" className="kgb-btn kgb-btn--quiet kgb-btn--danger" onClick={onRemove}>
          Remove path
        </button>
      </div>
    </AnchoredPanel>
  );
}
